type AuthLocale = typeof import('../en/auth').default

const auth = {
	login: {
		title: 'Iniciar sesión',
		metaTitle: 'Iniciar sesión en {{brandName}}',
		description: {
			line1: 'Introduce tu correo o usuario y contraseña para',
			line2: 'Acceder a tu cuenta',
		},
		usernameLabel: 'Usuario',
		usernameOrEmailLabel: 'Usuario o correo',
		passwordLabel: 'Contraseña',
		emailPlaceholder: 'nombre@ejemplo.com',
		usernameOrEmailPlaceholder: 'nombre@ejemplo.com o tu_usuario',
		rememberMe: 'Recordarme',
		forgotPasswordLink: '¿Olvidaste tu contraseña?',
		submit: 'Acceder',
		newHere: '¿Eres nuevo?',
		signupLink: 'Crea una cuenta',
		legalNotice: {
			prefix: 'Al iniciar sesión, aceptas nuestros',
			termsLink: 'Términos del servicio',
			connector: 'y la',
			privacyLink: 'Política de privacidad',
			suffix: '.',
		},
		passkey: {
			loginWithPasskey: 'Iniciar sesión con passkey',
			generatingAuthOptions: 'Generando opciones de autenticación',
			requestingAuthorization: 'Solicitando tu autorización',
			verifyingPasskey: 'Verificando tu passkey',
			loggedInNavigating: '¡Has iniciado sesión! Navegando...',
			unknownError: 'Error desconocido',
			failedToAuthenticate: 'No se pudo autenticar con passkey: {{error}}',
		},
	},
	signup: {
		title: 'Empieza gratis',
		metaTitle: 'Registrarse | {{brandName}}',
		description:
			'Introduce tu correo y te enviaremos un código de verificación.',
		alreadyHaveAccount: '¿Ya tienes una cuenta?',
		signIn: 'Iniciar sesión',
		emailLabel: 'Correo',
		emailHint:
			'Te enviaremos un enlace y un código. Revisa también spam o promociones.',
		submit: 'Enviar código de acceso',
		emailExistsLoginLink: 'Inicia sesión con este correo',
		legalNotice: {
			prefix: 'Al crear una cuenta, aceptas nuestros',
			termsLink: 'Términos del servicio',
			connector: 'y la',
			privacyLink: 'Política de privacidad',
			suffix: '.',
		},
		emailSubject: '¡Bienvenido a {{brandName}}!',
		emailHeading: '¡Bienvenido a {{brandName}}!',
		verificationCode: 'Tu código de verificación es:',
		clickToStart: 'O haz clic en el enlace para comenzar:',
	},
	forgotPassword: {
		title: 'Recuperar contraseña',
		metaTitle: 'Recuperación de contraseña de {{brandName}}',
		description: {
			line1: 'Introduce tu correo o usuario registrado y',
			line2: 'Te enviaremos un enlace para restablecer tu contraseña.',
		},
		usernameOrEmailLabel: 'Usuario o correo',
		submit: 'Recuperar contraseña',
		dontHaveAccount: '¿No tienes una cuenta?',
		signup: 'Regístrate',
		emailSubject: 'Restablecer contraseña de {{brandName}}',
		emailHeading: 'Restablecer contraseña de {{brandName}}',
		verificationCode: 'Tu código de verificación es:',
		clickLink: 'O haz clic en el enlace:',
	},
	resetPassword: {
		title: 'Restablecer contraseña',
		metaTitle: 'Restablecer contraseña | {{brandName}}',
		greeting: 'Hola, {{username}}. No te preocupes. Sucede todo el tiempo.',
		newPasswordLabel: 'Nueva contraseña',
		confirmPasswordLabel: 'Confirmar contraseña',
		submit: 'Restablecer contraseña',
	},
	verify: {
		title: 'Autenticación de dos factores',
		metaTitle: 'Autenticación de dos factores | {{brandName}}',
		description:
			'Introduce el código de autenticación que hemos enviado a tu correo.',
		checkEmailTitle: 'Revisa tu correo',
		checkEmailDescription:
			'Lo hemos enviado a tu correo. El enlace y el código caducan en 10 minutos. Revisa spam o promociones si no llega.',
		resetPasswordLinkDescription:
			'Te hemos enviado un enlace para restablecer tu contraseña. Ábrelo desde tu correo para crear una nueva contraseña.',
		check2FATitle: 'Revisa tu app de 2FA',
		check2FADescription:
			'Introduce tu código de 2FA para verificar tu identidad.',
		submit: 'Verificar',
		processingTitle: 'Verificando tu enlace...',
		processingDescription: 'Espera mientras completamos la verificación.',
		notReceived: '¿No lo has recibido?',
		resend: 'Reenviar un nuevo código',
		resendResetLink: 'Enviar un nuevo enlace',
		resendCountdown: 'Reenviar en {{seconds}} s',
		resendSuccess: 'Código reenviado. Revisa tu correo.',
		resendError: 'No hemos podido reenviar el código. Inténtalo de nuevo.',
		startOver: 'Volver a empezar',
		codeLabel: 'Código',
		crossDeviceHint:
			'Puedes abrir el enlace o copiar el código y escribirlo aquí.',
		resetPasswordLinkOnlyHint:
			'Si abriste el enlace en otro dispositivo o ha caducado, solicita un nuevo email de restablecimiento y usa el botón de ese correo.',
	},
	onboarding: {
		setupTitle: 'Configura tu cuenta {{brandShortName}}',
		title: '¡Bienvenido a bordo {{email}}!',
		description: 'Completa estos datos para guardar tu progreso.',
		usernameLabel: 'Usuario',
		usernameHint:
			'Te sugerimos uno a partir de tu correo. Usa de 3 a 20 caracteres: letras, números y guion bajo.',
		usernameSuggestionsLabel: 'Prueba una de estas opciones:',
		nameLabel: 'Nombre',
		passwordLabel: 'Contraseña',
		channelLabel: '¿Cómo conociste {{brandName}}? (opcional)',
		channelPlaceholder: 'Prefiero no decirlo',
		channelOptions: {
			search: 'Un buscador',
			delegation_or_school: 'Mi delegación o mi escuela',
			teacher: 'Un profesor',
			social: 'Redes sociales',
			friend: 'Un amigo',
			other: 'Otro',
		},
		confirmPasswordLabel: 'Confirmar contraseña',
		passwordRequirements: {
			title: 'Requisitos de contraseña',
			minLength: 'Al menos 6 caracteres',
			maxLength: 'Longitud compatible con almacenamiento seguro',
			match: 'Las dos contraseñas coinciden',
		},
		termsLabel: {
			prefix: 'Acepto los',
			termsLink: 'Términos del servicio',
			connector: 'y la',
			privacyLink: 'Política de privacidad',
			suffix: '.',
		},
		rememberMe: 'Recordarme',
		createAccount: 'Crear cuenta',
		youCanChangePhoto: 'Podrás cambiar tu foto más tarde',
		alt: {
			profile: 'Perfil',
		},
		welcomeToastTitle: 'Bienvenido',
		welcomeToastDescription: '¡Gracias por registrarte!',
	},
	errors: {
		invalidCredentials: 'Usuario, correo o contraseña inválidos',
		usernameRequired: 'El nombre de usuario es obligatorio',
		usernameTooShort: 'El nombre de usuario debe tener al menos 3 caracteres',
		usernameTooLong: 'El nombre de usuario no puede superar los 20 caracteres',
		usernameInvalidFormat:
			'El nombre de usuario solo puede incluir letras, números y guiones bajos',
		emailRequired: 'Escribe tu correo para continuar',
		emailInvalid: 'Revisa el formato: nombre@dominio.com',
		emailTooShort: 'El correo es demasiado corto',
		emailTooLong: 'El correo es demasiado largo',
		passwordRequired: 'La contraseña es obligatoria',
		passwordTooShort: 'La contraseña es demasiado corta',
		passwordTooLong: 'La contraseña es demasiado larga',
		passwordsMustMatch: 'Las contraseñas deben coincidir',
		nameRequired: 'El nombre es obligatorio',
		nameTooShort: 'El nombre es demasiado corto',
		nameTooLong: 'El nombre es demasiado largo',
		emailExists: 'Ya existe un usuario con este correo',
		usernameExists: 'Ya existe un usuario con este nombre de usuario',
		userNotFound: 'No existe un usuario con este nombre de usuario o correo',
		passwordTooCommon: 'La contraseña es demasiado común',
		mustAgree:
			'Debes aceptar los términos de servicio y la política de privacidad',
	},
} satisfies AuthLocale

export default auth
